import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { artworkApi as A, ART_STATUS_STYLE } from "@/lib/artwork";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Lock, CheckCircle2, XCircle, FlaskConical, GitBranch } from "lucide-react";

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [art, setArt] = useState(null);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prepress, setPrepress] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [thumb, setThumb] = useState("");
  const [dlg, setDlg] = useState(null); // 'approve' | 'reject' | 'strike'
  const [dlgVal, setDlgVal] = useState("");
  const canEdit = can("artwork", "edit");
  const canApprove = can("artwork", "approve");

  const load = async () => {
    setLoading(true);
    try { const a = await A.get(id); setArt(a); const v = a.versions.find((x) => x.version === a.current_version); setSel(v.version); setPrepress(v.prepress_notes || ""); setFileUrl(v.file_url || ""); setThumb(v.thumbnail_url || ""); }
    catch { toast.error("Not found"); navigate("/artwork"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !art) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  const ver = art.versions.find((v) => v.version === sel) || art.versions[0];

  const refresh = (a) => { setArt(a); const v = a.versions.find((x) => x.version === (a.current_version)); if (v) { setSel(v.version); setPrepress(v.prepress_notes || ""); setFileUrl(v.file_url || ""); setThumb(v.thumbnail_url || ""); } };
  const wrap = async (p, msg) => { try { const a = await p; refresh(a); toast.success(msg); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  const saveVersion = () => wrap(A.updateVersion(id, ver.version, { prepress_notes: prepress, file_url: fileUrl, thumbnail_url: thumb, status: ver.status === "Draft" ? "In Prepress" : undefined }), "Saved");
  const submitDlg = async () => {
    if (dlg === "approve") await wrap(A.approve(id, ver.version, { customer_contact: dlgVal }), "Approved");
    if (dlg === "reject") { if (!dlgVal) { toast.error("Reason required"); return; } await wrap(A.reject(id, ver.version, { reason: dlgVal }), "Rejected"); }
    if (dlg === "strike") await wrap(A.strikeOff(id, ver.version, { result: dlgVal, passed: true }), "Strike-off recorded");
    setDlg(null); setDlgVal("");
  };

  return (
    <div className="space-y-6" data-testid="artwork-detail-page">
      <button onClick={() => navigate("/artwork")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to artwork</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{art.name}</h2>
            <Badge className={`hover:bg-inherit ${ART_STATUS_STYLE[art.status]}`} data-testid="art-status-badge">{art.status}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-500">{art.artwork_code} · {art.customer_name} · {art.repeat_type || "—"} · {art.colours || "?"} colours{art.locked_version ? ` · Locked V${art.locked_version}` : ""}</p>
        </div>
        {canEdit && <Button variant="outline" onClick={() => wrap(A.addVersion(id), "Revision created")} data-testid="new-version-btn"><GitBranch className="mr-2 h-4 w-4" />New Revision</Button>}
      </div>

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        {/* Version list */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Versions</p>
          {art.versions.map((v) => (
            <button key={v.version} onClick={() => { setSel(v.version); setPrepress(v.prepress_notes || ""); setFileUrl(v.file_url || ""); setThumb(v.thumbnail_url || ""); }}
              data-testid={`version-tab-${v.version}`}
              className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm ${sel === v.version ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <span className="font-medium">V{v.version} {v.locked && <Lock className="ml-1 inline h-3 w-3 text-emerald-600" />}</span>
              <Badge className={`text-[10px] hover:bg-inherit ${ART_STATUS_STYLE[v.status]}`}>{v.status}</Badge>
            </button>
          ))}
        </div>

        {/* Version panel */}
        <Card className="p-6" data-testid="version-panel">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-slate-900">Version {ver.version}</h3>
            <div className="flex flex-wrap gap-2">
              {canEdit && !ver.locked && ver.status !== "Rejected" && <Button size="sm" variant="outline" onClick={() => setDlg("strike")} data-testid="strikeoff-btn"><FlaskConical className="mr-1 h-4 w-4" />Strike-off</Button>}
              {canApprove && ver.status !== "Approved" && ver.status !== "Locked" && ver.status !== "Rejected" && <Button size="sm" variant="outline" className="border-teal-300 text-teal-700" onClick={() => setDlg("approve")} data-testid="approve-btn"><CheckCircle2 className="mr-1 h-4 w-4" />Approve</Button>}
              {canEdit && !ver.locked && ver.status !== "Rejected" && <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => setDlg("reject")} data-testid="reject-btn"><XCircle className="mr-1 h-4 w-4" />Reject</Button>}
              {canApprove && ver.status === "Approved" && !ver.locked && <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => wrap(A.lock(id, ver.version), "Locked as print-ready")} data-testid="lock-btn"><Lock className="mr-1 h-4 w-4" />Lock (print-ready)</Button>}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-1.5"><Label>Print-ready file URL</Label><Input value={fileUrl} onChange={(e) => setFileUrl(e.target.value)} disabled={ver.locked} data-testid="file-url-input" placeholder="link to print-ready file" /></div>
            <div className="space-y-1.5"><Label>Thumbnail URL</Label><Input value={thumb} onChange={(e) => setThumb(e.target.value)} disabled={ver.locked} placeholder="preview image link" /></div>
            <div className="space-y-1.5 lg:col-span-2"><Label>Prepress notes</Label><Textarea value={prepress} onChange={(e) => setPrepress(e.target.value)} disabled={ver.locked} rows={2} /></div>
          </div>
          {thumb && <img src={thumb} alt="artwork" className="mt-4 h-28 w-28 rounded-md border object-cover" />}

          {ver.strike_off && <p className="mt-4 rounded-md bg-violet-50 p-3 text-sm text-violet-700" data-testid="strikeoff-info">Strike-off: {ver.strike_off.result || "recorded"}{ver.strike_off.passed ? " · passed" : ""}</p>}
          {ver.approval?.approved_at && <p className="mt-2 rounded-md bg-teal-50 p-3 text-sm text-teal-700">Customer approved by {ver.approval.by} on {new Date(ver.approval.approved_at).toLocaleDateString()}{ver.approval.customer_contact ? ` (${ver.approval.customer_contact})` : ""}</p>}
          {ver.approval?.rejected_at && <p className="mt-2 rounded-md bg-rose-50 p-3 text-sm text-rose-700">Rejected: {ver.approval.reason}</p>}

          {canEdit && !ver.locked && <Button className="mt-4 bg-indigo-600 text-white hover:bg-indigo-700" onClick={saveVersion} data-testid="save-version-btn">Save version</Button>}
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="mb-3 font-heading font-semibold text-slate-900">History</h3>
        <div className="space-y-2">
          {[...(art.history || [])].reverse().map((h) => (
            <div key={h.id} className="flex gap-3 border-b border-slate-100 pb-2 text-sm" data-testid="history-item">
              <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span>
              <span className="font-medium capitalize text-slate-700">{h.action}</span>
              <span className="text-slate-500">{h.detail} · {h.by}</span>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dlg === "approve" ? "Customer approval" : dlg === "reject" ? "Reject version" : "Record strike-off"}</DialogTitle>
            <DialogDescription>{dlg === "approve" ? "Record the customer's approval of this version." : dlg === "reject" ? "Provide a reason (a revision can then be created)." : "Record the strike-off trial result."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{dlg === "approve" ? "Customer contact (optional)" : dlg === "reject" ? "Reason" : "Result"}</Label>
            <Input value={dlgVal} onChange={(e) => setDlgVal(e.target.value)} data-testid="dlg-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={submitDlg} data-testid="dlg-confirm-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
